'use client';

import { Button } from '@magicschool/spellbook/components/Button';
import { cn } from '@magicschool/spellbook/lib/utils';

export type SkipToContentLinkProps = React.ComponentProps<typeof Button> & {
  /** The ID of the target element to skip to. Must match an actual element ID on the page for proper functionality. */
  contentId?: string;
};

/**
 * Visually hidden link that becomes visible on keyboard focus, allowing users to bypass navigation and jump directly to the main content. Required for WCAG 2.1 Guideline 2.4.1 compliance.
 *
 * ### When to use
 * - As the first focusable element in every page layout, before any navigation or header content
 * - In any layout that has navigation or repeated content before the main content area
 *
 * ### When NOT to use
 * - This component should not be omitted from layouts — it is a WCAG requirement
 * - For in-page anchor links visible to all users: use `Link` with an `href` anchor instead
 *
 * ### API
 * - `contentId`: The `id` of the target element to skip to. Defaults to `'main-content'`. The target element must exist on the page and have `tabIndex={-1}` for programmatic focus
 * - Inherits all `Button` props (`variant`, `size`, etc.). Defaults to `variant="ghost"` and `size="sm"`
 *
 * ### Implementation requirements
 * - Place as the very first child in your layout, before banners, navigation, or any other content
 * - The target element (e.g., `<main id="main-content" tabIndex={-1}>`) must exist on the page
 * - Provide localized children text (e.g., "Skip to main content")
 *
 * ### Visual states
 * - **Default**: Visually hidden via `sr-only` — invisible to sighted users, announced by screen readers
 * - **Focus**: Becomes visible as an absolutely positioned overlay at the top-left (LTR) or top-right (RTL) of the viewport
 *
 * ### Accessibility
 * - Renders as an anchor link (`<a href="#contentId">`) wrapped in a `Button` via `asChild`
 * - On activation, programmatically focuses the target element and scrolls to it
 * - WCAG 2.1 Guideline 2.4.1: Bypass Blocks — provides a mechanism to skip repeated navigation
 */
export const SkipToContentLink: React.FC<SkipToContentLinkProps> = ({
  contentId = 'main-content',
  children,
  className,
  onClick,
  ...props
}) => {
  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const element = document.getElementById(contentId);
    if (element) {
      element.focus();
    }
    onClick?.(e);
  };

  return (
    <Button
      data-spellbook-name="skip-to-content-link"
      {...props}
      variant="ghost"
      onClick={handleOnClick}
      className={cn(
        'sr-only',
        'opacity-0',
        'focus-visible:not-sr-only',
        'focus-visible:h-control-h-md', // not-sr-only resets height to auto, so we need to set it explicitly
        'focus-visible:absolute', // not-sr-only resets position to static, so we need to set it explicitly
        'focus-visible:opacity-100',
        'transition-opacity',
        'origin-center',
        'z-toast',
        'min-w-54',
        'top-3',
        'ltr:left-3',
        'rtl:right-3',
        '!bg-surface-overlay-0',
        className,
      )}
      asChild
    >
      <a href={`#${contentId}`}>{children}</a>
    </Button>
  );
};
